import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Loader2, ShieldCheck, AlertTriangle, Lock, ExternalLink, Calendar, CreditCard, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEUR = (n) =>
  `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const CommandePaymentPage = () => {
  const { commandeId, installmentIdx } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cgvAccepted, setCgvAccepted] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/commandes/${commandeId}/public/${installmentIdx}`);
        if (active) setData(r.data);
      } catch (e) {
        if (active) setError(e?.response?.data?.detail || 'Lien invalide ou expiré.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [commandeId, installmentIdx]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[70vh] flex items-center justify-center pt-24">
          <Loader2 className="h-10 w-10 animate-spin text-[#2A9D8F]" />
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <section className="min-h-[70vh] flex items-center justify-center pt-24 px-4">
          <div className="max-w-md w-full bg-white border border-rose-200 rounded-2xl p-8 text-center shadow-sm">
            <AlertTriangle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
            <h1 className="font-display text-2xl font-bold text-[#0A2540] mb-2">Lien invalide</h1>
            <p className="text-sm text-slate-600 mb-5">{error || 'Cette commande n\'existe pas.'}</p>
            <Button asChild className="bg-[#2A9D8F] hover:bg-[#1F7268] text-white">
              <Link to="/contact">Contactez-nous</Link>
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  const inst = data.installment;
  const isPaid = inst.status === 'paid';
  const hasLink = !!inst.square_payment_link;

  return (
    <Layout>
      <section className="bg-[#F5F3EF] min-h-screen pt-28 pb-16 px-4" data-testid="commande-payment-page">
        <div className="max-w-2xl mx-auto">
          {/* Header card */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-200">
            <div className="bg-gradient-to-br from-[#0A2540] to-[#1D3557] text-white px-6 sm:px-8 py-7 text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#C4956A] font-semibold mb-1.5">Paiement sécurisé</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold mb-1">
                {data.commande_number}
              </h1>
              <p className="text-sm text-white/70">Bonjour {data.client_name}</p>
            </div>

            <div className="p-6 sm:p-8 space-y-5">
              {/* Reservation recap */}
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Réservation</p>
                <p className="font-semibold text-[#0A2540]">{data.title}</p>
                {data.cruise_name && (
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" /> {data.cruise_name}
                  </p>
                )}
                {data.cruise_date_start && (
                  <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    Du {data.cruise_date_start}
                    {data.cruise_date_end && ` au ${data.cruise_date_end}`}
                  </p>
                )}
                <div className="mt-3 pt-3 border-t border-slate-200 text-sm text-slate-600">
                  Montant total de la commande : <strong className="text-[#0A2540]">{fmtEUR(data.total)}</strong>
                </div>
              </div>

              {/* Installment to pay */}
              <div className="bg-[#FFF8F2] border-2 border-[#C4724F]/30 rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#C4724F] mb-1">À régler maintenant</p>
                    <p className="font-semibold text-[#0A2540]">{inst.label}</p>
                    <p className="text-xs text-slate-500 mt-1">Échéance : {inst.due_date}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-3xl sm:text-4xl font-bold text-[#C4724F]">{fmtEUR(inst.amount)}</div>
                  </div>
                </div>
              </div>

              {/* Already paid */}
              {isPaid && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
                  <ShieldCheck className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
                  <p className="font-semibold text-emerald-800">Ce versement a déjà été réglé.</p>
                  <p className="text-sm text-emerald-700 mt-1">Merci pour votre paiement !</p>
                </div>
              )}

              {/* No link yet */}
              {!isPaid && !hasLink && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
                  <AlertTriangle className="h-8 w-8 text-amber-600 mx-auto mb-2" />
                  <p className="font-semibold text-amber-900">Lien de paiement non encore disponible.</p>
                  <p className="text-sm text-amber-800 mt-1">Veuillez contacter l'équipe SOGNUDIMARE.</p>
                </div>
              )}

              {/* CGV + CTA */}
              {!isPaid && hasLink && (
                <>
                  <label
                    htmlFor="cgv-checkbox"
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                      cgvAccepted ? 'border-[#2A9D8F] bg-[#2A9D8F]/5' : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                  >
                    <input
                      id="cgv-checkbox"
                      type="checkbox"
                      checked={cgvAccepted}
                      onChange={(e) => setCgvAccepted(e.target.checked)}
                      className="mt-0.5 h-5 w-5 rounded border-2 border-slate-300 text-[#2A9D8F] focus:ring-[#2A9D8F]"
                      data-testid="cgv-checkbox"
                    />
                    <div className="text-sm text-slate-700 leading-relaxed">
                      J'accepte cet échéancier et je reconnais avoir lu et accepté les{' '}
                      <Link to="/cgv" target="_blank" className="text-[#2A9D8F] font-semibold underline">
                        Conditions Générales de Vente
                      </Link>{' '}
                      de SOGNUDIMARE et confirme procéder au paiement de cette échéance.
                    </div>
                  </label>

                  <Button
                    asChild={cgvAccepted}
                    disabled={!cgvAccepted}
                    className={`w-full h-14 text-base font-semibold rounded-xl transition-all ${
                      cgvAccepted
                        ? 'bg-[#C4724F] hover:bg-[#A0552F] text-white shadow-lg hover:shadow-xl'
                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                    }`}
                    data-testid="pay-btn"
                  >
                    {cgvAccepted ? (
                      <a href={inst.square_payment_link} target="_blank" rel="noopener noreferrer">
                        <Lock className="h-4 w-4 mr-2" />
                        Payer {fmtEUR(inst.amount)} via Square
                        <ExternalLink className="h-4 w-4 ml-2" />
                      </a>
                    ) : (
                      <span className="flex items-center justify-center">
                        <Lock className="h-4 w-4 mr-2" />
                        Cocher les CGV pour continuer
                      </span>
                    )}
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-xs text-slate-400 pt-2">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    <span>Paiement 100 % sécurisé · Square</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Footer help */}
          <p className="text-center text-xs text-slate-500 mt-5">
            Une question ? Contactez-nous au{' '}
            <a href="tel:0495729028" className="text-[#2A9D8F] font-semibold">04 95 72 90 28</a>{' '}
            ou à{' '}
            <a href="mailto:contact@sognudimare-catamarans.com" className="text-[#2A9D8F] font-semibold">
              contact@sognudimare-catamarans.com
            </a>
          </p>
        </div>
      </section>
    </Layout>
  );
};

export default CommandePaymentPage;
